
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useArticleInteractions } from "@/hooks/useArticleInteractions";
import { MessageSquare, Heart, Bookmark, ThumbsDown, Share } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSupabase } from "@/hooks/useSupabase";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface ArticleInteractionsProps {
  articleId: string;
}

export const ArticleInteractions = ({ articleId }: ArticleInteractionsProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  
  const { handleLike, handleDislike, handleFavorite, isLiked, isFavorited } = useArticleInteractions(articleId);
  
  const { toast } = useToast();
  const { isLoggedIn, user } = useSupabase();
  const navigate = useNavigate();

  const requireAuth = async (action: () => Promise<void>, actionName: string) => {
    if (!isLoggedIn) {
      toast({
        title: "Authentication Required",
        description: `Please login to ${actionName}`,
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    
    try {
      if (actionName === "save articles") {
        setIsSaving(true);
      } else if (actionName === "like articles") {
        setIsLiking(true);
      }
      
      await action();
    } catch (error) {
      console.error(`Error while ${actionName}:`, error);
      toast({
        title: "Error",
        description: `Failed to ${actionName}. Please try again.`,
        variant: "destructive"
      });
    } finally {
      if (actionName === "save articles") {
        setIsSaving(false);
      } else if (actionName === "like articles") {
        setIsLiking(false);
      }
    }
  };

  // Share handler
  const handleShare = async () => {
    const url = window.location.origin + "/article/" + articleId;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Article link copied!",
        description: "You can now share it anywhere."
      });
    } catch {
      toast({
        title: "Could not copy link",
        description: url,
        variant: "destructive"
      });
    }
  };

  console.log("ArticleInteractions rendering for article:", articleId);
  console.log("User logged in:", isLoggedIn, "User ID:", user?.id);
  console.log("Article favorited status:", isFavorited);

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        <Button 
          variant="ghost" 
          size="icon" 
          className={cn("h-6 w-6 p-0", isLiked && "text-red-500")}
          onClick={() => requireAuth(handleLike, "like articles")} 
          title={isLiked ? "Unlike" : "Like"}
          disabled={isLiking}
        >
          <Heart className="h-4 w-4" fill={isLiked ? "currentColor" : "none"} />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6 p-0" 
          onClick={() => requireAuth(handleDislike, "dislike articles")} 
          title="Dislike"
        >
          <ThumbsDown className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className={cn("h-6 w-6 p-0", isFavorited && "text-yellow-500")}
          onClick={() => requireAuth(handleFavorite, "save articles")} 
          title={isFavorited ? "Unsave" : "Save"}
          disabled={isSaving}
        >
          <Bookmark className="h-4 w-4" fill={isFavorited ? "currentColor" : "none"} />
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6 p-0" onClick={handleShare} title="Share">
          <Share className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
